import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Drawer, Field, Select, StatusPill, formatNumber } from '@/components/live';
import { DeleteRecordButton } from '@/components/DeleteRecordButton';
import { callRpc, listRows } from '@/api/resources';
import { useRows } from '@/hooks/useResource';
import { toast } from '@/lib/toast';
import { isSupabaseConfigured } from '@/lib/supabase';
import { formatArea } from '@/lib/area';
import { ASSET_GROUP_COLOURS, type AssetShape, type AssetType } from '@/screens/parts/PlotMap';
import type { PlotFeatureRow } from '@/types/records';

const RISK = [
  { id: '', label: 'Not assessed' },
  { id: 'low', label: 'Low' }, { id: 'medium', label: 'Medium' },
  { id: 'high', label: 'High' }, { id: 'critical', label: 'Critical' },
];
const CONDITION = [
  { id: '', label: '--' }, { id: 'good', label: 'Good' }, { id: 'fair', label: 'Fair' },
  { id: 'poor', label: 'Poor' }, { id: 'unusable', label: 'Unusable' },
];

/**
 * Assets and risks on a plot - the risk assessment layer (owner, 2026-09-21: roads, wells,
 * temples, burial grounds, ponds, houses, rest areas, toilets, electrical lines, HCV areas,
 * fire extinguishers, first aid, emergency contacts ...).
 *
 * The map marks WHERE; this asks WHAT: a name, the risk level, what is being done about it,
 * a buffer to keep clear (a riparian strip, a line's clearance), and condition. The shape is
 * checked on the server against the type (a well is a point, a road a line).
 */
export function usePlotAssets(plotId: string | null | undefined) {
  const types = useQuery({
    queryKey: ['ref', 'plot_asset_types'],
    queryFn: () => listRows<AssetType & { sort_order: number }>('plot_asset_types', { orderBy: { column: 'sort_order' } }),
    enabled: isSupabaseConfigured && Boolean(plotId),
  });
  const rows = useRows<PlotFeatureRow & AssetRowExtras>('plot_features', { filters: { plot_id: plotId ?? undefined } }, Boolean(plotId));
  const groupOf = (typeId: string | null) => types.data?.find((type) => type.id === typeId)?.asset_group ?? null;
  const shapes: AssetShape[] = (rows.data ?? []).map((row) => ({
    id: row.id,
    label: row.name ?? row.type_label ?? 'Asset',
    group: groupOf(row.type_id),
    geometry: row.geometry as AssetShape['geometry'],
  }));
  return { types, rows, shapes, groupOf };
}

interface AssetRowExtras {
  severity_or_sensitivity?: string | null;
  mitigation_status?: string | null;
  buffer_m?: number | null;
  area_ha?: number | null;
  length_m?: number | null;
}

export function PlotAssetsPanel({
  plotId,
  pending,
  onDone,
  rows,
  groupOf,
}: {
  plotId: string;
  /** A shape just marked on the map, waiting for its details. */
  pending: { type: AssetType; geometry: { type: string; coordinates: unknown } } | null;
  onDone: () => void;
  rows: Array<PlotFeatureRow & AssetRowExtras>;
  groupOf: (typeId: string | null) => string | null;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: '', severity: '', mitigation: '', buffer: '', condition: '', notes: '' });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!pending) return;
    setSaving(true);
    try {
      await callRpc('fn_save_plot_feature', {
        p_plot_id: plotId,
        p_type_id: pending.type.id,
        p_geojson: pending.geometry,
        p_name: form.name.trim() || null,
        p_severity: form.severity || null,
        p_mitigation: form.mitigation.trim() || null,
        p_buffer_m: form.buffer ? Number(form.buffer) : null,
        p_condition: form.condition || null,
        p_description: form.notes.trim() || null,
      });
      await queryClient.invalidateQueries({ queryKey: ['plot_features'] });
      toast.success(`${pending.type.label} marked.`);
      setForm({ name: '', severity: '', mitigation: '', buffer: '', condition: '', notes: '' });
      onDone();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <section className="plot-assets">
        <h3 className="section-title">Assets & risks ({rows.length})</h3>
        {rows.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            None marked yet. Use "Mark asset / risk" above the map - inside the plot or on its boundary.
          </p>
        ) : (
          <ul className="asset-list">
            {rows.map((row) => {
              const group = groupOf(row.type_id);
              return (
                <li key={row.id}>
                  <span className="asset-swatch" style={{ background: ASSET_GROUP_COLOURS[group ?? ''] ?? '#e5e7eb' }} />
                  <span className="asset-main">
                    <strong>{row.name ?? row.type_label}</strong>
                    <small className="muted">
                      {row.type_label}{group ? ` · ${group}` : ''}
                      {row.area_ha != null ? ` · ${formatArea(row.area_ha)}` : ''}
                      {row.length_m != null ? ` · ${formatNumber(row.length_m, 0)} m long` : ''}
                      {row.buffer_m != null ? ` · keep ${formatNumber(row.buffer_m, 0)} m clear` : ''}
                    </small>
                    {row.mitigation_status && <small>Mitigation: {row.mitigation_status}</small>}
                  </span>
                  {row.severity_or_sensitivity && <StatusPill value={row.severity_or_sensitivity} />}
                  <DeleteRecordButton entity="feature" id={row.id} invalidate={['plot_features']}
                                      label={row.name ?? row.type_label ?? 'asset'} onDeleted={() => undefined}
                                      screenKey="plot.mapping" />
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <Drawer
        footer={
          <>
            <button onClick={onDone} type="button">Discard</button>
            <button className="primary" disabled={saving} onClick={() => void save()} type="button">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </>
        }
        onClose={onDone}
        open={Boolean(pending)}
        subtitle={pending ? `${pending.type.asset_group} · marked as a ${pending.type.geometry_type}` : undefined}
        title={pending ? pending.type.label : 'Asset'}
      >
        <Field hint="Optional - e.g. 'North well', 'Village road to Karimpur'." label="Name">
          <input onChange={(event) => setForm({ ...form, name: event.target.value })} value={form.name} />
        </Field>
        <div className="field-row">
          <Field label="Risk level">
            <Select onChange={(value) => setForm({ ...form, severity: value })} options={RISK} value={form.severity} />
          </Field>
          <Field label="Condition">
            <Select onChange={(value) => setForm({ ...form, condition: value })} options={CONDITION} value={form.condition} />
          </Field>
        </div>
        <Field hint="What is done, or needed, to control the risk." label="Mitigation">
          <input onChange={(event) => setForm({ ...form, mitigation: event.target.value })}
                 placeholder="e.g. Fenced; no spraying within 10 m" value={form.mitigation} />
        </Field>
        <Field hint="Distance to keep clear around it, if any." label="Buffer (m)">
          <input inputMode="decimal" onChange={(event) => setForm({ ...form, buffer: event.target.value })} value={form.buffer} />
        </Field>
        <Field hint="Emergency contact numbers, extinguisher type and expiry, observations..." label="Notes">
          <textarea onChange={(event) => setForm({ ...form, notes: event.target.value })} rows={3} value={form.notes} />
        </Field>
      </Drawer>
    </>
  );
}
